
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Settings, Save, RotateCcw, Eye, EyeOff, Layout, Grip } from "lucide-react";
import { useDashboardLayout } from "@/contexts/DashboardLayoutContext";
import { toast } from "sonner";

const DashboardCustomizer = () => {
  const { 
    widgets, 
    toggleWidgetVisibility, 
    saveLayout, 
    resetLayout,
    isCustomizing,
    setIsCustomizing
  } = useDashboardLayout();

  const handleSave = async () => {
    await saveLayout();
    setIsCustomizing(false);
    toast.success("تم حفظ تخطيط لوحة التحكم بنجاح");
  };

  const handleReset = () => {
    resetLayout();
    toast.info("تم إعادة تعيين التخطيط للقيم الافتراضية");
  };

  const visibleWidgets = widgets.filter(w => w.isVisible).length;

  if (!isCustomizing) {
    return (
      <div className="fixed bottom-6 left-6 z-50">
        <Button
          onClick={() => setIsCustomizing(true)}
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
          size="lg"
        >
          <Settings className="w-5 h-5 ml-2" />
          تخصيص لوحة التحكم
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 left-6 z-50 max-w-sm">
      <Card className="bg-white/95 backdrop-blur-sm border-slate-200 shadow-xl">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layout className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-slate-800">تخصيص العرض</h3>
            </div>
            <Badge variant="secondary">
              {visibleWidgets}/{widgets.length} ظاهر
            </Badge>
          </div>

          {/* رسالة توضيحية */}
          <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded border border-blue-200">
            <div className="flex items-center gap-1 mb-1">
              <Grip className="w-3 h-3" />
              <span className="font-medium">استخدم أيقونة السحب لإعادة الترتيب</span>
            </div>
            <span>يمكنك تشغيل/إيقاف عرض أي قسم من الأقسام أدناه</span>
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {widgets
              .sort((a, b) => a.order - b.order)
              .map((widget) => (
              <div key={widget.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-2">
                  {widget.isVisible ? (
                    <Eye className="w-4 h-4 text-green-600" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  )}
                  <span className="text-sm font-medium text-slate-700">
                    {widget.title}
                  </span>
                </div>
                <Switch
                  checked={widget.isVisible}
                  onCheckedChange={() => toggleWidgetVisibility(widget.id)}
                />
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-2 border-t">
            <Button onClick={handleSave} className="flex-1" size="sm">
              <Save className="w-4 h-4 ml-1" />
              حفظ
            </Button>
            <Button onClick={handleReset} variant="outline" size="sm">
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button 
              onClick={() => setIsCustomizing(false)} 
              variant="ghost" 
              size="sm"
            >
              إلغاء
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardCustomizer;
